import * as yup from 'yup';
import { OptionType } from '../CommonComponents/OPL/Select/BaseSelect/types';

export enum AllocationStrategyType {
  MarketAssumptions,
  PortfolioGroup,
  HypotheticalReturn,
  ExistingAccount,
  AdviserSettings,
}

export interface AssetClassChart {
  name: string;
  y: number;
  color_group: number;
  color?: string;
}

export interface Item {
  Id: number;
  GroupId: number;
  PortfolioName: string;
  PortfolioDescription: string;
  HighLevelChart: AssetClassChart[];
  DetailedAssetAllocation: AssetClassChart[];
}

export interface Group {
  GroupId: number;
  GroupName: string;
  ModelPortfolios: Item[];
}

export enum ExternalSystemId {
  Orion = 2,
  Riskalyze = 5,
}

export interface AssetClassDTO {
  color: string | null;
  color_group: number;
  id: number;
  name: string | null;
  y: number;
}

export interface ModelDTO {
  assetAllocationInputModel: AssetClassDTO[];
  riskScore: number | null;
  hypotheticalReturn: number | null;
  id: string;
  orionModelId: number | null;
  portfolioDescription: string | null;
  portfolioName: string;
  riskRange: {
    from: number;
    to: number;
  };
}

export interface ModelGroupDTO {
  adviserId: number | null;
  createdBy: {
    name: string;
    email: string;
  } | null;
  externalSystem: ExternalSystemId | null;
  groupId: string;
  groupName: string;
  modelPortfolios: ModelDTO[];
}

export interface EarmarkValueDTO {
  earmarkType: number;
  dependent: {
    dependentType: number;
    id: string;
    isDependent: boolean;
    personalInfo: {
      contactInformation: string | null;
      dob: string;
      firstName: string;
      lastName: string;
      yearOfBirth: number | null;
    };
  } | null;
  name: string | null;
}

export interface ManageExcessCashDTO {
  modelPortfoliosGroups: ModelGroupDTO[];
  options: {
    accountList: OptionType<string>[];
    defaultAssetAllocation: AssetClassDTO[];
    earmarkOptions: OptionType<EarmarkValueDTO>[];
    hasOrionModels: boolean;
    excessCashAllocation: {
      excessCashAllocationDetails: AllocationStrategyData | null;
      excessCashAllocationType: AllocationStrategyType;
      excessCashGoalEarmark: EarmarkValueDTO[] | null;
    };
    excessCashAllocationTypes: { label: string; value: AllocationStrategyType }[];
  };
}

export interface AllocationStrategyData {
  marketAssumptions: AssetClassDTO[] | null;
  modelPortfolios: ModelDTO | null;
  hypotheticalReturnPercent: number | null;
  existingAccountId: string | null;
}

export interface FormValues {
  ExcessCashGoalEarmark: EarmarkValueDTO[] | null;
  ExcessCashFlowStrategy: {
    type: AllocationStrategyType;
    data: AllocationStrategyData | null;
  };
}

export const FORM_ID = 'client-settings--manage-excess-cash-modal-form';

export const MODAL_HEADER_ID = 'client-settings--manage-excess-cash-modal-header';

export const validation = yup.object().shape({
  ExcessCashFlowStrategy: yup.object({
    type: yup.mixed<AllocationStrategyType>().required('Source is required'),

    data: yup.mixed().when('type', (type, schema) => {
      switch (type) {
        case AllocationStrategyType.MarketAssumptions:
          return yup.object({
            marketAssumptions: yup
              .array<OptionType<string>[]>()
              .test('check-total', 'The total must add upto 100%', arr => {
                if (!arr || !Array.isArray(arr)) return false;
                const sum = arr.reduce((acc, obj) => acc + (parseFloat(obj.y) || 0), 0);
                return sum >= 100;
              }),
          });
        case AllocationStrategyType.PortfolioGroup:
          return yup.object({
            modelPortfolios: yup.object({
              id: yup
                .string()
                .nullable()
                .required('Portfolio Name is required'),
            }),
          });
        case AllocationStrategyType.HypotheticalReturn:
          return yup.object({
            hypotheticalReturnPercent: yup
              .number()
              .required('Hypothetical Return Percentage is required')
              .typeError('The value must be between 0% and 100%')
              .nullable()
              .transform((value, originalValue) =>
                String(originalValue).trim() === '' ? null : value,
              )
              .min(0, 'The value must be between 0% and 100%')
              .max(100, 'The value must be between 0% and 100%'),
          });
        case AllocationStrategyType.ExistingAccount:
          return yup.object({
            existingAccountId: yup
              .string()
              .required('Account Name is required')
              .nullable(),
          });
        default:
          return yup.mixed().nullable();
      }
    }),
  }),
});
